import featuredListingsMarker from 'images/featured_listings_marker.png';
import featuredRentalsMarker from 'images/featured_rentals_marker.png';
import featuredSalesMarker from 'images/featured_sales_marker.png';
import propertyMarker from 'images/property_marker.png';
import {getIn} from 'lib/immutableObject';
import {checkIfMarkersChanged, fitBounds} from 'lib/MapUtil';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import {Component} from 'react';
import './ComparableMap.scss';

export default class ComparableMap extends Component {
  static propTypes = {
    targetLatLng: PropTypes.shape({lat: PropTypes.number.isRequired, lng: PropTypes.number.isRequired}),
    center: PropTypes.shape({lat: PropTypes.number.isRequired, lng: PropTypes.number.isRequired}),
    zoom: PropTypes.number,
    markers: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.oneOf(['listings', 'sales','rentals']).isRequired,
      label: PropTypes.string,
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }).isRequired).isRequired,
    onMapZoomChange: PropTypes.func.isRequired,
    onMapCenterChange: PropTypes.func.isRequired,
  };

  componentDidMount() {
    const {targetLatLng, center, zoom} = this.props;

    if (targetLatLng && getIn(window, ['google', 'maps'])) {
      const map = this._map = new window.google.maps.Map(this._mapEl, {
        center: center || targetLatLng,
        zoom: zoom || 16,
        disableDefaultUI: true,
        scaleControl: true,
        zoomControl: true,
        clickableIcons: false,
      });

      this._targetMarker = new window.google.maps.Marker({
        map,
        position: targetLatLng,
        icon: {
          scaledSize: {width: 39, height: 33},
          url: propertyMarker,
        },
      });

      this.makeMarkers();

      window.google.maps.event.addListener(map, 'center_changed', this.onCenterChanged);
      window.google.maps.event.addListener(map, 'zoom_changed', this.onZoomChanged);
    }
  }

  componentDidUpdate(prevProps) {
    const {center, zoom, markers} = this.props;

    if (this._map && !isEqual(center, prevProps.center)) {
      this._map.setCenter(center);
    }

    if (this._map && zoom !== prevProps.zoom) {
      this._map.setZoom(zoom);
    }

    if (checkIfMarkersChanged(markers, prevProps.markers)) {
      this.makeMarkers(markers);
    }
  }

  componentWillUnmount() {
    if (this._map) {
      window.google.maps.event.clearInstanceListeners(this._map);
      this._markers.forEach(m => m.setMap(null));
      this._targetMarker.setMap(null);
    }
  }

  _markers = [];

  makeMarkers = () => {
    const {markers, targetLatLng} = this.props;
    const savedCenter = this.props.center;
    const savedZoom = this.props.zoom;

    if (this._map) {
      const marker = {
        sales: featuredSalesMarker,
        listings: featuredListingsMarker,
        rentals: featuredRentalsMarker,
      };
      this._markers.forEach(m => m.setMap(null));
      this._markers = markers.map(({type, label, lat, lng}) => new window.google.maps.Marker({
        map: this._map,
        position: {lat, lng},
        icon: {
          scaledSize: {width: 39, height: 33},
          url: marker[type],
          labelOrigin: {x: 16, y: 13},
        },
        label: label ? {text: label, color: '#ffffff'} : undefined,
      }));

      const allLatLng = [...markers, targetLatLng];
      const northEast = {
        lat: Math.min(...allLatLng.map(({lat}) => lat)),
        lng: Math.min(...allLatLng.map(({lng}) => lng)),
      };
      const southWest = {
        lat: Math.max(...allLatLng.map(({lat}) => lat)),
        lng: Math.max(...allLatLng.map(({lng}) => lng)),
      };

        const {center, zoom} = fitBounds({
          latLng1: northEast,
          latLng2: southWest,
          dimensions: {width: this._mapEl.offsetWidth, height: this._mapEl.offsetHeight},
          targetPixelsBetweenPoints: Math.min(this._mapEl.offsetWidth, this._mapEl.offsetHeight) - 80
        });

        this._map.setCenter(savedCenter || center);
        this._map.setZoom(savedZoom || zoom);
    }
  };

  onCenterChanged = debounce(() => {
    const latLng = this._map.getCenter();
    this.props.onMapCenterChange({lat: latLng.lat(), lng: latLng.lng()});
  }, 500);

  onZoomChanged = debounce(() => {
    this.props.onMapZoomChange(this._map.getZoom());
  }, 500);

  render() {
    const {targetLatLng} = this.props;

    return (
      <div className="ComparableMap">
        <div className="map" ref={(ref) => this._mapEl = ref}>
          {(!targetLatLng) && (
            <div className="no-data">This property contains incomplete location data</div>
          )}
        </div>
        <div className="legend">
          <div>
            <img src={propertyMarker} alt=""/>
            Your Property
          </div>
          <div>
            <img src={featuredListingsMarker} alt=""/>
            For Sale
          </div>
          <div>
            <img src={featuredSalesMarker} alt=""/>
            Recently Sold
          </div>
        </div>
      </div>
    );
  }
}
